import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";

/** COMPONENT */
import { Box, IconButton, MenuItem, Tooltip, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SubjectIcon from "@mui/icons-material/Subject";
import HeaderCard from "../../component/cardCustom/headerCard";
import TableCustom from "../../component/table/tableCustom";
import TableRowCommon from "../../component/table/TableRowCommon";
import InputTextField from "../../component/input/inputTextField";
import InputDatePicker from "../../component/input/inputDatePicker";
import FilterSelect from "../../component/select/filterSelect";
import AutocompleteSelect from "../../component/select/autoCompleteSelect";
import ButtonCustom from "../../component/button/buttonCustom";
import Loading from "../../component/loading/loading";
import { notiError } from "../../component/notification/notifications";

/** MODAL */
import ModalStallReclaimCreate from "./modal/ModalStallReclaimCreate";
import ModalStallReclaimDetail from "./modal/ModalStallReclaimDetail";
import ModalStallReclaimReview from "./modal/ModalStallReclaimReview";
import ModalStallReclaimLog from "./modal/ModalStallReclaimLog";

/** API */
import StallReclaimApi, {
  StallReclaimRow,
  StallReclaimSummary,
} from "../../api/stall-reclaim/stall-reclaim.api";

/** UTILS */
import { colors } from "../../constants/colors";
import { MENUS_FIRST } from "../../constants/menu";
import {
  STALL_RECLAIM_FILTER_ALL,
  STALL_RECLAIM_STATUS,
  STALL_RECLAIM_STATUS_ALL,
  STALL_RECLAIM_STATUS_OPTIONS,
  stallReclaimStatusBg,
  stallReclaimStatusColor,
  stallReclaimStatusLabel,
} from "../../constants/stallReclaim";
import { dateTimeToView } from "../../utils/date.utils";
import { getApiErrorMessage } from "../../utils/api-error.utils";
import { DASH, dateOnlyToView } from "../depositRefund/depositRefund.utils";
import { useMenuPermission } from "../depositRefund/useMenuPermission";
import { useStallReclaimFilter } from "./useStallReclaimFilter";

/** STYLE */
import * as Style from "./stallReclaim.style";

const EMPTY_SUMMARY: StallReclaimSummary = {
  all: 0,
  WAITING: 0,
  APPROVE: 0,
  NOT_APPROVE: 0,
};

/** ฝั่งเจ้าหน้าที่ — ออกคำสั่งเรียกคืนแผงค้าและพิจารณาคำสั่ง (ไม่มีฝั่งผู้เช่า) */
export default function StallReclaim() {
  const permissions = useMenuPermission(MENUS_FIRST.STALL_RECLAIM);
  const filter = useStallReclaimFilter();

  const [data, setData] = useState<StallReclaimRow[]>([]);
  const [summary, setSummary] = useState<StallReclaimSummary>(EMPTY_SUMMARY);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** modal — ใช้ standalone mode ทุกตัว จึงต้อง conditional render จากตรงนี้ */
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [reviewRow, setReviewRow] = useState<StallReclaimRow | null>(null);
  const [logRow, setLogRow] = useState<StallReclaimRow | null>(null);

  const { buildTableParams, buildBaseParams } = filter;

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await StallReclaimApi.findAll(buildTableParams());
      // envelope ของ endpoint ชุดนี้ซ้อน data สองชั้น ไม่ใช่ res.data ตรงๆ แบบ API รุ่นเก่า
      const paginated = res.data?.data;
      setData(paginated?.items || []);
      setRowCount(paginated?.meta?.totalItems || 0);
    } catch (error) {
      setData([]);
      setRowCount(0);
      notiError(getApiErrorMessage(error, "ดึงรายการคำสั่งเรียกคืนแผงค้าไม่สำเร็จ"));
    } finally {
      setIsLoading(false);
    }
  }, [buildTableParams]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await StallReclaimApi.summary(buildBaseParams());
      setSummary(res.data?.data || EMPTY_SUMMARY);
    } catch (error) {
      // ตัวเลขข้าง dropdown ไม่ใช่ข้อมูลหลัก พลาดแล้วให้เงียบ ไม่รบกวนซ้ำกับ error ของตาราง
      setSummary(EMPTY_SUMMARY);
    }
  }, [buildBaseParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /** เรียกหลังสร้าง/พิจารณาสำเร็จ ให้ทั้งตารางและตัวเลขสถานะตรงกัน */
  const reload = () => {
    loadData();
    loadSummary();
  };

  const statusCount = (value: string) =>
    value === STALL_RECLAIM_STATUS_ALL
      ? summary.all
      : summary[value as keyof StallReclaimSummary] ?? 0;

  const headCells = [
    { id: "reclaimNo", disablePadding: false, label: "เลขที่คำสั่ง", align: "center" },
    { id: "leaseAgreementNo", disablePadding: false, label: "เลขที่สัญญา", align: "center" },
    { id: "tenantName", disablePadding: false, label: "ชื่อผู้เช่าแผงค้า", align: "left" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "left" },
    { id: "stallName", disablePadding: false, label: "แผงค้า", align: "left" },
    { id: "stallNumber", disablePadding: false, label: "เลขแผงค้า", align: "center" },
    { id: "stallTypeName", disablePadding: false, label: "ประเภทแผงค้า", align: "left" },
    { id: "requestNote", disablePadding: false, label: "รายละเอียด", align: "left" },
    { id: "requestDate", disablePadding: false, label: "วันเวลาที่สร้าง", align: "center" },
    { id: "dueDate", disablePadding: false, label: "กำหนดคืนพื้นที่", align: "center" },
    { id: "status", disablePadding: false, label: "สถานะ", align: "center" },
    { id: "action", disablePadding: false, label: "Action", align: "center" },
  ];

  const renderAction = (objData: StallReclaimRow) => {
    const isWaiting = objData.status === STALL_RECLAIM_STATUS.WAITING;
    const viewDisabled = !permissions.isView;
    // พิจารณาได้เฉพาะคำสั่งที่ยังรอ — แถวอื่นให้ปุ่มจางและกดไม่ได้ตามดีไซน์
    const reviewDisabled = !isWaiting || !permissions.isApporve;

    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Tooltip title="รายละเอียดคำสั่งเรียกคืนแผงค้า">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setDetailId(objData.id_deposit_refund)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <DescriptionOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isWaiting ? "พิจารณาคำสั่ง" : "คำสั่งนี้ถูกพิจารณาแล้ว"}>
          <span>
            <IconButton
              disabled={reviewDisabled}
              onClick={() => setReviewRow(objData)}
              sx={{ color: reviewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ประวัติการพิจารณา">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setLogRow(objData)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <SubjectIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const renderData = (objData: StallReclaimRow, no: number) => {
    const objRenderData = {
      key: String(objData.id_deposit_refund),
      id: String(objData.id_deposit_refund),
      obj: objData,
      columns: [
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.LinkText onClick={() => setDetailId(objData.id_deposit_refund)}>
              {objData.deposit_refund_no || DASH}
            </Style.LinkText>
          ),
        },
        { option: "TEXT", align: "center", label: objData.lease_agreement_no || DASH },
        { option: "TEXT", align: "left", label: objData.tenant_name || DASH },
        { option: "TEXT", align: "left", label: objData.zone_name || DASH },
        { option: "TEXT", align: "left", label: objData.stall_name || DASH },
        { option: "TEXT", align: "center", label: objData.stall_number || DASH },
        { option: "TEXT", align: "left", label: objData.stall_type_name || DASH },
        { option: "TEXT", align: "left", label: objData.request_note || DASH },
        // dateTimeToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
        {
          option: "TEXT",
          align: "center",
          label: objData.request_date ? dateTimeToView(objData.request_date) : DASH,
        },
        // due_date เป็น DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok
        // ผิดไป 1 วันในเรื่องกำหนดคืนพื้นที่มีผลทางกฎหมาย
        { option: "TEXT", align: "center", label: dateOnlyToView(objData.due_date) },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.StatusChip
              chipColor={stallReclaimStatusColor(objData.status)}
              chipBg={stallReclaimStatusBg(objData.status)}
            >
              {stallReclaimStatusLabel(objData.status)}
            </Style.StatusChip>
          ),
        },
        { option: "COMPONENT", align: "center", component: renderAction(objData) },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <>
      {isLoading && <Loading show type="softLoading" />}
      <HeaderCard text="เรียกคืนแผงค้า" />
      <Style.MainContainer>
        <Row className="align-items-end">
          <Col lg={3} md={6} xs={12} className="my-1">
            <InputTextField
              value={filter.searchInput}
              placeholder="ค้นหาเลขที่คำสั่ง / ผู้เช่าแผงค้า / เลขที่สัญญา"
              onchange={(e: any) => filter.setSearchInput(e.target.value)}
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            {/*
              ใช้ autocomplete ไม่ใช่ select เพราะแผงค้าในสาขาเดียวมีหลายร้อยรายการ เลื่อนหาไม่ไหว
              (โซน/ประเภทแผงค้าใช้แบบเดียวกันเพื่อให้แถวตัวกรองหน้าตาเสมอกัน)
              state ในฮุคยังเก็บเป็น id เหมือนเดิม แปลง id <-> option object ที่จุดนี้จุดเดียว
              กดกากบาทล้างค่า = กลับไปเป็น "ทั้งหมด" ตาม sentinel เดิม
            */}
            <AutocompleteSelect
              labelId="stall-reclaim-zone"
              inputLabel="โซน"
              options={filter.zones}
              // ต้องเป็น null ไม่ใช่ undefined ไม่งั้น MUI สลับไปโหมด uncontrolled แล้วค่าค้าง
              value={filter.zones.find((zone) => zone.zoneId === filter.zoneId) || null}
              getOptionLabel={(option: any) => option?.zoneName || DASH}
              isOptionEqualToValue={(option: any, value: any) => option?.zoneId === value?.zoneId}
              onChange={(event: any, value: any) =>
                filter.handleChangeZone(value?.zoneId ?? STALL_RECLAIM_FILTER_ALL)
              }
              noOptionsText="ไม่พบโซน"
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <AutocompleteSelect
              labelId="stall-reclaim-stall"
              inputLabel="แผงค้า"
              // ลิสต์นี้ถูกกรองตามโซนที่เลือกแล้ว (ยังไม่เลือกโซน = แผงของทุกโซน)
              options={filter.stallOptions}
              value={filter.stallOptions.find((stall) => stall.stallId === filter.stallId) || null}
              getOptionLabel={(option: any) => (option ? filter.stallOptionLabel(option) : "")}
              // ค้นได้ทั้งเลขแผงและชื่อร้าน — ไม่ระบุ stringify เอง MUI จะกรองด้วย label เท่านั้น
              filterOptions={(option: any) =>
                `${option?.stallNumber || ""} ${option?.stallName || ""}`
              }
              isOptionEqualToValue={(option: any, value: any) => option?.stallId === value?.stallId}
              onChange={(event: any, value: any) =>
                filter.handleChangeStall(value?.stallId ?? STALL_RECLAIM_FILTER_ALL)
              }
              noOptionsText="ไม่พบแผงค้า"
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <AutocompleteSelect
              labelId="stall-reclaim-stall-type"
              inputLabel="ประเภทแผงค้า"
              options={filter.stallTypes}
              value={
                filter.stallTypes.find((type) => type.stallTypeId === filter.stallTypeId) || null
              }
              getOptionLabel={(option: any) => option?.stallTypeNameTh || DASH}
              isOptionEqualToValue={(option: any, value: any) =>
                option?.stallTypeId === value?.stallTypeId
              }
              onChange={(event: any, value: any) =>
                filter.handleChangeStallType(value?.stallTypeId ?? STALL_RECLAIM_FILTER_ALL)
              }
              noOptionsText="ไม่พบประเภทแผงค้า"
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="stall-reclaim-status"
              selectId="stall-reclaim-status"
              value={filter.status}
              onchange={(event: any) => filter.handleChangeStatus(event.target.value)}
              renderValue={() => `สถานะ : ${stallReclaimStatusLabel(filter.status)}`}
              options={STALL_RECLAIM_STATUS_OPTIONS.map((option: string) => (
                <MenuItem key={option} value={option}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: stallReclaimStatusColor(option),
                        }}
                      />
                      {stallReclaimStatusLabel(option)}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 14, color: colors.textLightGray }}>
                      {statusCount(option)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <InputDatePicker
              value={filter.dateStart || null}
              onChange={filter.handleChangeDateStart}
              onClear={() => filter.handleChangeDateStart(null)}
              allowClear
              label="วันที่เริ่มต้น"
              placeholder="เลือกวันที่"
              disableEndDate={filter.dateEnd || undefined}
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <InputDatePicker
              value={filter.dateEnd || null}
              onChange={filter.handleChangeDateEnd}
              onClear={() => filter.handleChangeDateEnd(null)}
              allowClear
              label="วันที่สิ้นสุด"
              placeholder="เลือกวันที่"
              disableStartDate={filter.dateStart || undefined}
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <ButtonCustom
              mode="add"
              style={{ width: "100%" }}
              textButton="ทำรายการ"
              disabled={!permissions.isCreate}
              onClick={() => setOpenCreate(true)}
            />
          </Col>
        </Row>

        <Style.TableContainer>
          <TableCustom
            headCells={headCells}
            customScroll
            page={filter.page}
            pageLimit={filter.pageLimit}
            sortType="ASC"
            sortBy=""
            rowCount={rowCount}
            rowsData={data.map((row, index) => renderData(row, index))}
            // API ชุดนี้ยังไม่รองรับการเรียงลำดับ หัวตารางจึงไม่มีปุ่ม sort
            onSort={() => {}}
            setPageLimit={filter.handleChangePageLimit}
            setPage={(value: number) => filter.setPage(value)}
            textEmptyData="ไม่พบคำสั่งเรียกคืนแผงค้า"
            tableMinWidth={1800}
          />
        </Style.TableContainer>
      </Style.MainContainer>

      {openCreate && (
        <ModalStallReclaimCreate
          onClose={() => setOpenCreate(false)}
          onSuccess={() => {
            setOpenCreate(false);
            reload();
          }}
        />
      )}

      {detailId !== null && (
        <ModalStallReclaimDetail id={detailId} onClose={() => setDetailId(null)} />
      )}

      {reviewRow && (
        <ModalStallReclaimReview
          row={reviewRow}
          onClose={() => setReviewRow(null)}
          onSuccess={() => {
            setReviewRow(null);
            reload();
          }}
        />
      )}

      {logRow && (
        <ModalStallReclaimLog
          id={logRow.id_deposit_refund}
          subTitle={[logRow.deposit_refund_no, logRow.tenant_name, logRow.stall_number]
            .filter(Boolean)
            .join(" · ")}
          userId={logRow.userId}
          stallId={logRow.stallId}
          onClose={() => setLogRow(null)}
        />
      )}
    </>
  );
}
